import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PublicApi } from '../constants/public-api-constants';

export interface CompanionNumber {
  id: number;
  phone_number: string;
  is_verified: boolean;
  companion_enabled: boolean;
  is_primary: boolean;
  verified_at?: string | null;
  last_active_at?: string | null;
  created_at?: number | string;
}


/**
 * One row of the Companion activity feed. Only the fields the `whatsapp_logs`
 * filters prove exist (`action`, `severity`, `user_id`, plus the usual id /
 * created_at) are relied on; everything else is read through a fallback chain in
 * `normalizeLog` because the endpoint's row shape is not otherwise pinned down.
 * `raw` keeps the untouched record so the detail drawer can always show the
 * complete payload, whatever the backend actually sends.
 */
export interface WhatsappLog {
  id: number | string | null;
  created_at: number | string | null;
  action: string;
  severity: string;
  user_id: number | null;
  phone_number: string;
  direction: string;
  message: string;
  status: string;
  error_message: string;
  raw: any;
}

/** Query accepted by `whatsapp_logs` (GET). Empty strings mean "no filter". */
export interface WhatsappLogsQuery {
  page: number;
  per_page: number;
  action?: string;
  severity?: string;
  user_id?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface WhatsappLogsPage {
  items: WhatsappLog[];
  total: number;
  page: number;
}

export interface WhatsappConversationLogQuery {
  report?: 'chat' | 'tokens' | string;
  search?: string;
  phone?: string;
  company_id?: number;
  user_id?: number;
  direction?: string;
  intent?: string;
  category?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface WhatsappConversationLogItem {
  id: number | string | null;
  created_at: number | string | null;
  phone: string;
  company_id: number | null;
  user_id: number | null;
  direction: string;
  intent: string;
  category: string;
  body: string;
  message: string;
  tokens_used: number;
  raw: any;
}

export interface TokenReportItem {
  id?: number | string;
  user_id?: number;
  company_id?: number;
  user_name?: string;
  company_name?: string;
  phone?: string;
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_messages?: number;
  created_at?: number | string;
  raw: any;
}

export interface WhatsappConversationLogResultPage {
  items: any[];
  total: number;
  page: number;
  pageSize: number;
}

export interface WhatsappPendingContestQuery {
  page?: number;
  per_page?: number;
  search?: string;
}

export interface WhatsappPendingContestsResultPage {
  items: any[];
  total: number;
  page: number;
  perPage: number;
}

/**
 * Campaign Companion (WhatsApp bot) connection management.
 * Talks to the Xano `luqta_whatsapp` group (user-authenticated dashboard calls).
 */
@Injectable({ providedIn: 'root' })
export class CompanionService {
  private http = inject(HttpClient);

  /** List the current user's connected / pending WhatsApp numbers. */
  listNumbers(): Observable<CompanionNumber[]> {
    return this.http.get<CompanionNumber[]>(PublicApi.COMPANION_NUMBERS_LIST);
  }

  /** Enable / disable the Companion for a verified number. */
  toggleNumber(numberId: number, enabled: boolean): Observable<CompanionNumber> {
    return this.http.post<CompanionNumber>(PublicApi.COMPANION_NUMBER_TOGGLE, {
      number_id: numberId,
      companion_enabled: enabled,
    });
  }

  /** Remove (disconnect) a number. */
  removeNumber(numberId: number): Observable<{ isSuccess: boolean; removed_id: number }> {
    return this.http.post<{ isSuccess: boolean; removed_id: number }>(
      PublicApi.COMPANION_NUMBER_REMOVE,
      { number_id: numberId },
    );
  }

  /**
   * Account-level master switch — separate from the per-number toggles above.
   * The GET has been seen returning both a bare boolean and a wrapper object, so
   * normalize here rather than making every caller guess.
   */
  isCompanionEnabled(): Observable<boolean> {
    return this.http
      .get<any>(PublicApi.COMPANION_IS_ENABLED)
      .pipe(map((res) => this.readEnabledFlag(res)));
  }

  setCompanionEnabled(enabled: boolean): Observable<boolean> {
    return this.http
      .post<any>(PublicApi.COMPANION_ENABLE, { companion_enabled: enabled })
      .pipe(map((res) => this.readEnabledFlag(res, enabled)));
  }

  private readEnabledFlag(res: any, fallback = false): boolean {
    if (typeof res === 'boolean') return res;
    if (res && typeof res === 'object') {
      const value = res.companion_enabled ?? res.enabled ?? res.is_enabled ?? res.result;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') return value === 'true';
    }
    return fallback;
  }

  /** Enable/disable Companion for a user (self or, as admin, a team member); unlink revokes numbers. */
  setEnabled(targetUserId: number, enabled: boolean, unlink = false): Observable<any> {
    return this.http.post<any>(PublicApi.COMPANION_SET_ENABLED, {
      target_user_id: targetUserId,
      enabled,
      unlink,
    });
  }

  /**
   * Companion activity log feed. This is a GET with query params — a POST to the
   * same path 404s — so the filter object is flattened onto the query string.
   */
  listLogs(query: WhatsappLogsQuery): Observable<WhatsappLogsPage> {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      // 0 is a real value here (`user_id: 0` means "every user"), so only
      // undefined/null/'' count as "filter not set".
      if (value === undefined || value === null || value === '') return;
      params = params.set(key, String(value));
    });

    return this.http
      .get<any>(PublicApi.COMPANION_LOGS, { params })
      .pipe(map((res) => this.normalizeLogsPage(res, query.page)));
  }

  /**
   * Xano hands paged lists back as `{ items, itemsTotal, curPage }`, but the
   * other log endpoint in this app answers `{ data: { logs, pagination } }` and a
   * bare array is possible too — accept all three rather than betting on one.
   */
  private normalizeLogsPage(res: any, requestedPage: number): WhatsappLogsPage {
    const body = res?.data ?? res;
    const rows: any[] = Array.isArray(body)
      ? body
      : body?.items ?? body?.logs ?? body?.records ?? [];

    const pagination = body?.pagination;
    const total =
      body?.itemsTotal ??
      pagination?.total_records ??
      body?.total ??
      rows.length;
    const page = body?.curPage ?? pagination?.current_page ?? requestedPage;

    return {
      items: rows.map((row) => this.normalizeLog(row)),
      total: Number(total) || 0,
      page: Number(page) || requestedPage,
    };
  }

  private normalizeLog(row: any): WhatsappLog {
    const pick = (...keys: string[]): string => {
      for (const key of keys) {
        const value = row?.[key];
        if (value !== undefined && value !== null && value !== '') return String(value);
      }
      return '';
    };

    return {
      id: row?.id ?? null,
      created_at: row?.created_at ?? row?.createdAt ?? row?.timestamp ?? null,
      action: pick('action', 'event', 'event_type', 'type'),
      severity: pick('severity', 'level', 'log_level'),
      user_id: row?.user_id ?? row?.userId ?? null,
      phone_number: pick('phone_number', 'phone', 'from', 'to', 'whatsapp_number', 'msisdn'),
      direction: pick('direction', 'flow'),
      message: pick('message', 'message_text', 'body', 'text', 'description'),
      status: pick('status', 'state', 'result'),
      error_message: pick('error_message', 'error', 'failure_reason'),
      raw: row,
    };
  }

  /**
   * Whatsapp Conversation Log API (report: "chat" or "tokens").
   * Sends JSON body payload via POST (as expected by Xano POST endpoints), with GET fallback.
   */
  getWhatsappConversationLog(query: WhatsappConversationLogQuery): Observable<WhatsappConversationLogResultPage> {
    const payload = {
      report: query.report || 'chat',
      search: query.search || '',
      phone: query.phone || '',
      company_id: query.company_id ? Number(query.company_id) : 0,
      user_id: query.user_id ? Number(query.user_id) : 0,
      direction: query.direction || '',
      intent: query.intent || '',
      category: query.category || '',
      date_from: query.date_from || '',
      date_to: query.date_to || '',
      page: query.page || 1,
      page_size: query.page_size || 25,
    };

    let params = new HttpParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params = params.set(key, String(value));
    });

    return this.http
      .post<any>(PublicApi.WHATSAPP_CONVERSATION_LOG, payload)
      .pipe(
        map((res) => this.normalizeConversationLogPage(res, query.page || 1, query.page_size || 25))
      );
  }

  private normalizeConversationLogPage(res: any, requestedPage: number, pageSize: number): WhatsappConversationLogResultPage {
    const body = res?.data ?? res;
    const rows: any[] = Array.isArray(body)
      ? body
      : body?.items ?? body?.logs ?? body?.records ?? body?.results ?? [];

    const pagination = body?.pagination;
    const total =
      body?.itemsTotal ??
      body?.total_records ??
      pagination?.total_records ??
      body?.total ??
      rows.length;
    const page = body?.curPage ?? pagination?.current_page ?? requestedPage;

    return {
      items: rows,
      total: Number(total) || 0,
      page: Number(page) || requestedPage,
      pageSize: Number(body?.perPage ?? pagination?.per_page ?? pageSize) || pageSize,
    };
  }

  /**
   * WhatsApp Pending Contests API.
   * Sends query params via GET (or query object) with page, per_page, search.
   */
  getWhatsappPendingContests(query: WhatsappPendingContestQuery = {}): Observable<WhatsappPendingContestsResultPage> {
    let params = new HttpParams();
    const page = query.page !== undefined && query.page !== null ? query.page : 0;
    const per_page = query.per_page !== undefined && query.per_page !== null ? query.per_page : 0;
    const search = query.search ?? '';

    params = params.set('page', String(page));
    params = params.set('per_page', String(per_page));
    if (search) {
      params = params.set('search', search);
    }

    return this.http
      .get<any>(PublicApi.WHATSAPP_PENDING_CONTESTS, { params })
      .pipe(
        map((res) => this.normalizePendingContestsPage(res, page || 1, per_page || 10))
      );
  }

  private normalizePendingContestsPage(res: any, requestedPage: number, requestedPerPage: number): WhatsappPendingContestsResultPage {
    const body = res?.data ?? res;
    const rows: any[] = Array.isArray(body)
      ? body
      : body?.items ?? body?.contests ?? body?.records ?? body?.results ?? [];

    const pagination = body?.pagination;
    const total =
      body?.itemsTotal ??
      body?.total_records ??
      pagination?.total_records ??
      body?.total ??
      rows.length;
    const page = body?.curPage ?? pagination?.current_page ?? requestedPage;
    const perPage = body?.perPage ?? pagination?.per_page ?? requestedPerPage;

    return {
      items: rows,
      total: Number(total) || 0,
      page: Number(page) || requestedPage,
      perPage: Number(perPage) || requestedPerPage,
    };
  }

}

// ─────────────────────────────────────────────────────────────────────────────
// Client WhatsApp conversation log (`whatsapp_client_conversation_log`)
//
// One POST endpoint serves every screen; the `resource` field in the body picks
// what comes back. Scoping is server-side from the auth token — sending a
// user_id does nothing, and asking for a number that is not connected to the
// logged-in account comes back as `ok: false, status: 403`.
// ─────────────────────────────────────────────────────────────────────────────

export type ClientLogResource =
  | 'logs'
  | 'conversation'
  | 'me'
  | 'numbers'
  | 'dashboard'
  | 'conversations'
  | 'chat-sessions'
  | 'session-messages';

/**
 * The endpoint answers HTTP 200 even when it refuses, so the `ok` flag in the
 * envelope is the real status. This carries a refusal into the normal RxJS
 * error channel.
 */
export class ClientLogApiError extends Error {
  constructor(public reason: string, public status: number) {
    super(reason);
    this.name = 'ClientLogApiError';
  }
}

/**
 * A single WhatsApp message row.
 *
 * `id`, `session_id` and `contest_id` are Postgres BIGINTs and arrive as
 * strings — never compare them to numbers with `===`.
 */
export interface ClientLogItem {
  id: string;
  phone_number: string;
  direction: string;
  body: string;
  intent: string | null;
  category: string | null;
  row_count: number | null;
  contest_id: string | null;
  session_id: string | null;
  message_sid: string | null;
  created_at: string;
}

export interface ClientLogsPage {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  limit: number;
  offset: number;
  items: ClientLogItem[];
}

/** Filters for the `logs` feed. Empty strings and 0 mean "no filter". */
export interface ClientLogsQuery {
  page?: number;
  page_size?: number;
  phone?: string;
  direction?: string;
  search?: string;
  session_id?: number;
  date_from?: string;
  date_to?: string;
}

export interface ClientLogNumber {
  phone: string;
  verified: boolean;
  is_primary: boolean;
  companion_enabled: boolean;
  source: string;
  last_active_at: string | null;
}

export interface ClientLogAccount {
  user_id: number;
  company_id: number;
  name: string | null;
  email: string | null;
  country: string | null;
  company_name: string | null;
  companion_enabled: boolean;
  numbers: ClientLogNumber[];
  /** Only these numbers have readable history. */
  readable_numbers: string[];
}

export interface ClientLogNumberStat {
  phone: string;
  source: string;
  verified: boolean;
  is_primary: boolean;
  companion_enabled: boolean;
  readable: boolean;
  display_name: string | null;
  language: string | null;
  state: string | null;
  total_messages: number;
  inbound: number;
  outbound: number;
  sessions: number;
  contests_created: number;
  first_message_at: string | null;
  last_message_at: string | null;
}

export interface ClientLogDashboard {
  numbers: number;
  totals: {
    messages: number;
    inbound: number;
    outbound: number;
    messages_today: number;
    messages_24h: number;
    sessions: number;
    contests: number;
  };
  messages_per_day: { day: string; total: number; inbound: number; outbound: number }[];
  intents: { intent: string; n: number }[];
  per_number: { phone_number: string; messages: number; last_message_at: string }[];
  window_days: number;
}

export interface ClientLogConversation {
  phone_number: string;
  last_message: string | null;
  last_direction: string | null;
  display_name: string | null;
  language: string | null;
  state: string | null;
  message_count: number;
  last_message_at: string;
  created_at: string;
}

export interface ClientLogChatSession {
  id: string;
  phone_number: string;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
  message_count: number;
  start_reason: string;
  end_reason: string | null;
  open: boolean;
}

export interface ClientLogSessionMessages {
  session_id: number;
  phone: string;
  items: ClientLogItem[];
}

/**
 * Read-only access to the logged-in client's own WhatsApp history.
 *
 * Kept separate from CompanionService because this is the client-scoped
 * endpoint: it never needs an account id from the caller, and every response
 * shares one `{ ok, data }` envelope.
 */
@Injectable({ providedIn: 'root' })
export class ClientConversationLogService {
  private http = inject(HttpClient);

  /** Posts one resource request and unwraps the envelope. */
  private call<T>(body: Record<string, any>): Observable<T> {
    return this.http
      .post<any>(PublicApi.WHATSAPP_CLIENT_CONVERSATION_LOG, body)
      .pipe(
        map((res) => {
          if (!res || res.ok !== true) {
            throw new ClientLogApiError(
              res?.reason || 'Unable to read WhatsApp history.',
              Number(res?.status) || 0
            );
          }
          return res.data as T;
        })
      );
  }

  /** Main feed, newest first. */
  getLogs(query: ClientLogsQuery = {}): Observable<ClientLogsPage> {
    return this.call<ClientLogsPage>({
      resource: 'logs',
      page: query.page || 1,
      page_size: query.page_size || 25,
      phone: query.phone || '',
      direction: query.direction || '',
      search: query.search || '',
      session_id: query.session_id || 0,
      date_from: query.date_from || '',
      date_to: query.date_to || '',
    });
  }

  /** One number's transcript, oldest first — the chat view reads top-to-bottom. */
  getConversation(phone: string, page = 1, pageSize = 50): Observable<ClientLogsPage> {
    return this.call<ClientLogsPage>({
      resource: 'conversation',
      phone,
      page,
      page_size: pageSize,
    });
  }

  /** The signed-in account and the numbers it can read. */
  getAccount(): Observable<ClientLogAccount> {
    return this.call<ClientLogAccount>({ resource: 'me' });
  }

  /** Per-number counters. */
  getNumberStats(): Observable<ClientLogNumberStat[]> {
    return this.call<ClientLogNumberStat[]>({ resource: 'numbers' });
  }

  /** Totals and per-day series. `days` defaults to 7 server-side, max 90. */
  getDashboard(days = 30): Observable<ClientLogDashboard> {
    return this.call<ClientLogDashboard>({ resource: 'dashboard', days });
  }

  /** Chat list for the sidebar. */
  getConversations(): Observable<ClientLogConversation[]> {
    return this.call<ClientLogConversation[]>({ resource: 'conversations' });
  }

  /** Session boundaries; `ended_at: null` means the session is still open. */
  getChatSessions(phone = '', page = 1, pageSize = 25): Observable<ClientLogChatSession[]> {
    return this.call<ClientLogChatSession[]>({
      resource: 'chat-sessions',
      phone,
      page,
      page_size: pageSize,
    });
  }

  /** Every message inside one session. */
  getSessionMessages(sessionId: number | string): Observable<ClientLogSessionMessages> {
    return this.call<ClientLogSessionMessages>({
      resource: 'session-messages',
      session_id: Number(sessionId),
    });
  }

  /**
   * `messages_per_day` omits days with no traffic. Charts need a continuous
   * axis, so pad the gaps with zeroes.
   */
  fillMessageDayGaps(
    series: { day: string; total: number; inbound: number; outbound: number }[],
    days: number
  ): { day: string; total: number; inbound: number; outbound: number }[] {
    const byDay = new Map(series.map((d) => [d.day, d]));
    const out: { day: string; total: number; inbound: number; outbound: number }[] = [];
    const cursor = new Date();
    cursor.setUTCHours(0, 0, 0, 0);
    cursor.setUTCDate(cursor.getUTCDate() - (days - 1));

    for (let i = 0; i < days; i++) {
      const key = cursor.toISOString().slice(0, 10);
      out.push(byDay.get(key) || { day: key, total: 0, inbound: 0, outbound: 0 });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return out;
  }
}
